import {
  ApiResponse,
  RequestState,
  setErrorResponse,
} from 'shared/auth/hooks/useRequestState';
import { LogCardProps } from 'shared/components/EmailLogs/LogCard';
import {
  faPaperPlane,
  faInboxIn,
  faEnvelopeOpen,
  faHandPointer,
  faQuestion,
  faFaceConfused,
} from '@fortawesome/pro-light-svg-icons';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { SESEventLog } from './eventUtils';
import { FormatUTC } from 'shared/utils/formatUTC';

export interface IPLookup {
  isProxy: boolean | 'Unknown' | 'Loading';
  source: string;
  res: {
    address: string;
    city: string;
    country: string;
    isp: string;
    latitude: string;
    longitude: string;
    organization: string;
    stateprov: string;
    tzName: string;
    tzOffset: string;
  };
}

const getErrorResponse = (req: XMLHttpRequest) =>
  setErrorResponse({
    status: req.status || 0,
    message:
      req.statusText || 'Please turn off Cross Origin Policy in your browser.',
    isAxiosError: false,
    name: 'Custom Created Error',
  });

export const ipLookup = (ip: string): Promise<ApiResponse<IPLookup>> => {
  return new Promise<ApiResponse<IPLookup>>((resolve, reject) => {
    const http = new XMLHttpRequest();
    http.open('POST', 'https://www.iplocation.net/get-ipdata', true);
    http.setRequestHeader(
      'Content-type',
      'application/x-www-form-urlencoded; charset=UTF-8',
    );
    http.onreadystatechange = () => {
      if (http.readyState == 4 && http.status == 200) {
        try {
          const response = JSON.parse(http.responseText) as IPLookup;
          response.res.tzName = response.res['tz_name'];
          response.res.tzOffset = response.res['tz_offset'];
          resolve({ success: true, data: response });
        } catch (e) {
          resolve({
            success: false,
            error: getErrorResponse(http),
          });
        }
      } else if (
        (http.status > 299 || http.status < 199) &&
        http.readyState == 4
      ) {
        resolve({ success: false, error: getErrorResponse(http) });
      }
    };
    http.send(`ip=${ip}&source=dbip&ipv=4`);
  });
};

export const parseIPInfo = (ipInfo: IPLookup) => {
  const result: {
    info: React.ReactNode;
    label: React.ReactNode;
  }[] = [];
  result.push({
    label: '-',
    info: '-',
  });
  result.push({
    label: 'Is Proxy',
    info:
      ipInfo.isProxy === 'Loading' || ipInfo.isProxy === 'Unknown'
        ? ipInfo.isProxy
        : ipInfo.isProxy
        ? 'true'
        : 'false',
  });
  result.push({
    label: 'IP Organization',
    info: ipInfo.res.organization,
  });
  result.push({
    label: 'Internet Service Provider',
    info: ipInfo.res.isp,
  });
  result.push({
    label: 'Location',
    info: `${ipInfo.res.city}, ${ipInfo.res.stateprov}, ${ipInfo.res.country}`,
  });
  result.push({
    label: 'Timezone',
    info: `${ipInfo.res.tzName} (${ipInfo.res.tzOffset})`,
  });
  result.push({
    label: 'Lat Long',
    info: `${ipInfo.res.latitude}, ${ipInfo.res.longitude}`,
  });
  return result;
};

const isEventSuccess = (event: SESEventLog['eventType']): boolean =>
  event === 'Send' ||
  event === 'Open' ||
  event === 'Click' ||
  event === 'Delivery';

const getIcon = (
  event: SESEventLog['eventType'],
): IconDefinition | IconProp => {
  switch (event) {
    case 'Bounce':
      return faFaceConfused;
    case 'Click':
      return faHandPointer;
    case 'Open':
      return faEnvelopeOpen;
    case 'Delivery':
      return faInboxIn;
    case 'Send':
      return faPaperPlane;

    default:
      return faQuestion;
  }
};

const getTimeStamp = (event: SESEventLog) => {
  const defaultTimeStamp = new FormatUTC(event.timeStamp).formatDate();
  switch (event.eventType) {
    case 'Open':
      return new FormatUTC(event.openTimeStamp).formatDate();
    case 'Delivery':
      return new FormatUTC(event.deliveryTimeStamp).formatDate();
    case 'Send':
    case 'Bounce':
    default:
      return defaultTimeStamp;
  }
};

export interface EventLogs {
  messageIds: string[];
  events: {
    [id: string]: LogCardProps;
  };
}

export const toRenderLog = (events: SESEventLog[]): EventLogs => {
  const result: EventLogs = { messageIds: [], events: {} };
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    const event = {
      originalEvent: e,
      eventName: e.eventType,
      icon: getIcon(e.eventType),
      id: e.messageId,
      isSuccess: isEventSuccess(e.eventType),
      timestamp: getTimeStamp(e) || 'Unknown',
    };
    if (result.events[e.messageId]) {
      result.events[e.messageId].timelineData.push(event);
    } else {
      result.messageIds.push(e.messageId);
      result.events[e.messageId] = {
        messageId: e.messageId,
        org: '',
        email: e.destination,
        name: e.subject,
        timelineData: [event],
      };
    }
  }
  return result;
};

const unknownText = 'Unknown';
const loadingText = 'Loading...';
export const getIPInfo = (
  request: RequestState<IPLookup>,
): IPLookup | undefined => {
  switch (request.type) {
    case 'REQUEST_ERROR':
      return {
        isProxy: 'Unknown',
        source: unknownText,
        res: {
          address: unknownText,
          city: unknownText,
          country: unknownText,
          isp: unknownText,
          latitude: unknownText,
          longitude: unknownText,
          organization: unknownText,
          stateprov: unknownText,
          tzName: unknownText,
          tzOffset: unknownText,
        },
      };
    case 'REQUEST_START':
      return {
        isProxy: 'Loading',
        source: loadingText,
        res: {
          address: loadingText,
          city: loadingText,
          country: loadingText,
          isp: loadingText,
          latitude: loadingText,
          longitude: loadingText,
          organization: loadingText,
          stateprov: loadingText,
          tzName: loadingText,
          tzOffset: loadingText,
        },
      };
    case 'REQUEST_SUCCESS':
      return request.data;

    default:
    case 'REQUEST_INIT':
      return undefined;
  }
};
